import React, { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { openIosGuide } from "./IosInstallGuide";

const VISITS_KEY = "pwa_visits";
const DISMISS_KEY = "pwa_banner_dismissed_at";
const MIN_VISITS = 3;
const SNOOZE_MS = 14 * 24 * 3600 * 1000;

const isMobile = () => /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) || window.matchMedia("(max-width: 768px)").matches;
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

function countVisit() {
  let n = parseInt(localStorage.getItem(VISITS_KEY) || "0", 10) || 0;
  if (!sessionStorage.getItem("pwa_visit_counted")) {
    n += 1;
    localStorage.setItem(VISITS_KEY, String(n));
    sessionStorage.setItem("pwa_visit_counted", "1");
  }
  return n;
}

export const InstallBanner = () => {
  const [show, setShow] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    const visits = countVisit();
    const dismissed = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10) || 0;
    if (isMobile() && !isStandalone() && visits >= MIN_VISITS && Date.now() - dismissed > SNOOZE_MS) setShow(true);
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => setShow(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setShow(false); };
  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    setPrompt(null);
    if (outcome === "accepted") setShow(false); else dismiss();
  };

  if (!show) return null;
  return (
    <div className="fixed inset-x-3 bottom-3 z-50 rounded-2xl bg-brand-greenDeep text-white shadow-2xl p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4 fade-in duration-500" data-testid="pwa-install-banner">
      <img src="/icon-192.png" alt="" className="w-10 h-10 rounded-xl shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Installer l'app 360immoinvest</p>
        {prompt ? (
          <p className="text-xs text-white/70 mt-0.5">Accès rapide depuis votre écran d'accueil et notifications en temps réel.</p>
        ) : isIOS ? (
          <p className="text-xs text-white/70 mt-0.5 flex items-center gap-1 flex-wrap">Touchez <Share className="w-3.5 h-3.5 inline" /> <b>Partager</b> puis <b>« Sur l'écran d'accueil »</b>. <button onClick={openIosGuide} className="underline font-semibold text-brand-lime" data-testid="pwa-banner-ios-guide">Voir comment faire</button></p>
        ) : (
          <p className="text-xs text-white/70 mt-0.5">Menu du navigateur → <b>« Installer l'application »</b>.</p>
        )}
        {prompt && (
          <button onClick={install} className="mt-2.5 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-xs font-semibold transition-colors" data-testid="pwa-banner-install-btn">
            <Download className="w-3.5 h-3.5" /> Installer
          </button>
        )}
      </div>
      <button onClick={dismiss} aria-label="Fermer" className="p-1 rounded-full hover:bg-white/10 transition-colors shrink-0" data-testid="pwa-banner-dismiss-btn"><X className="w-4 h-4" /></button>
    </div>
  );
};
